import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Input, Select, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Page } from "@zhihaoscm/entity";
import { useEffect, useMemo, useState } from "react";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { DEFAULT_VALUE } from "@/config";
import { FUNCTION_MODULE, IS_ENTER, MATCH_SCORE_SHIP, SHIP_PLATFORM } from "@/meta/logistics/goodsAndShipAnalysis";

export interface ShipAnalysisQuery {
    page: number;
    size: number;
    operationPlatform?: number;
    functionModule?: number;
    startOperateTime?: string;
    endOperateTime?: string;
    resultCountMin?: number;
    resultCountMax?: number;
    isGenerateWaybill?: number;
    /** 旧 Vue 列表的匹配分数筛选参数。 */
    score?: number;
}

export interface ShipAnalysisRow {
    trackId?: string | number;
    operator?: string;
    operationPlatform?: number;
    functionModule?: number;
    operateTime?: string;
    matchRange?: number | string;
    matchShipNum?: number;
    scoreNinetyToHundred?: number;
    scoreEightyToNinety?: number;
    scoreSeventyToEighty?: number;
    scoreSixtyToSeventy?: number;
    invitationQuotationCount?: number;
    viewRequirementNum?: number;
    submitQuotationNum?: number;
    isGenerateWaybill?: number;
}

export interface ShipAnalysisPageProps {
    loadRows?: (query: ShipAnalysisQuery) => Promise<Page<ShipAnalysisRow>>;
}

/** Vue 匹配船舶分析 API adapter。 */
export async function loadShipAnalysis(query: ShipAnalysisQuery): Promise<Page<ShipAnalysisRow>> {
    const { shipAnalysis } = await import("@/api/services/shipAnalysis");
    return shipAnalysis.getAnalysisInspectionShipPaging(query);
}

const defaultQuery: ShipAnalysisQuery = { page: 1, size: 10 };
const text = (value: unknown) => (value == null || value === "" ? DEFAULT_VALUE : String(value));
const enumText = (meta: { getIntroByCode: (code?: number | string) => string }, value?: number) =>
    meta.getIntroByCode(value) || DEFAULT_VALUE;
// 复用旧 Vue 匹配分数元数据，确保界面文案与提交值始终来自同一份契约。
const matchScoreOptions = MATCH_SCORE_SHIP.options.map(item => ({ label: item.label, value: Number(item.value) }));

/** 匹配船舶分析最小列表，沿用旧 Vue 的筛选字段和日期提交格式。 */
export function ShipAnalysisPage({ loadRows = loadShipAnalysis }: ShipAnalysisPageProps) {
    const [filters, setFilters] = useState({
        operationPlatform: undefined as number | undefined,
        functionModule: undefined as number | undefined,
        startDate: "",
        endDate: "",
        resultCountMin: "",
        resultCountMax: "",
        isGenerateWaybill: undefined as number | undefined,
        score: undefined as number | undefined
    });
    const [query, setQuery] = useState<ShipAnalysisQuery>(defaultQuery);
    const [rows, setRows] = useState<ShipAnalysisRow[]>([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadRows(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setError("匹配船舶分析加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadRows, query]);

    const submit = () => {
        const next: ShipAnalysisQuery = { page: 1, size: 10 };
        if (filters.operationPlatform !== undefined) next.operationPlatform = filters.operationPlatform;
        if (filters.functionModule !== undefined) next.functionModule = filters.functionModule;
        if (filters.startDate) next.startOperateTime = `${filters.startDate} 00:00:00`;
        if (filters.endDate) next.endOperateTime = `${filters.endDate} 23:59:59`;
        if (filters.resultCountMin) next.resultCountMin = Number(filters.resultCountMin);
        if (filters.resultCountMax) next.resultCountMax = Number(filters.resultCountMax);
        if (filters.isGenerateWaybill !== undefined) next.isGenerateWaybill = filters.isGenerateWaybill;
        // 保持旧 Vue 的匹配分数筛选参数名，直接传递元数据值给分页接口。
        if (filters.score !== undefined) next.score = filters.score;
        setQuery(next);
    };

    const reset = () => {
        setFilters({
            operationPlatform: undefined,
            functionModule: undefined,
            startDate: "",
            endDate: "",
            resultCountMin: "",
            resultCountMax: "",
            isGenerateWaybill: undefined,
            score: undefined
        });
        setQuery(defaultQuery);
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 匹配船舶分析 ProTable 的当前展示字段。
    const analysisColumns = useMemo<TableColumnProps<ShipAnalysisRow>[]>(
        () => [
            { key: "trackId", label: "编号", width: 92, prop: "trackId", render: ({ row }) => text(row.trackId) },
            {
                key: "operator",
                label: "操作用户",
                width: 204,
                prop: "operator",
                render: ({ row }) => text(row.operator)
            },
            {
                key: "operationPlatform",
                label: "操作端",
                prop: "operationPlatform",
                render: ({ row }) => enumText(SHIP_PLATFORM, row.operationPlatform)
            },
            {
                key: "functionModule",
                label: "功能模块",
                prop: "functionModule",
                render: ({ row }) => enumText(FUNCTION_MODULE, row.functionModule)
            },
            {
                key: "operateTime",
                label: "操作时间",
                width: 204,
                prop: "operateTime",
                render: ({ row }) => text(row.operateTime?.replace("T", " "))
            },
            {
                key: "matchRange",
                label: "匹配范围（NM）",
                width: 148,
                prop: "matchRange",
                render: ({ row }) => text(row.matchRange)
            },
            {
                key: "matchShipNum",
                label: "匹配船舶结果数",
                prop: "matchShipNum",
                render: ({ row }) => text(row.matchShipNum)
            },
            {
                key: "scoreNinetyToHundred",
                label: "90-100分",
                prop: "scoreNinetyToHundred",
                render: ({ row }) => text(row.scoreNinetyToHundred)
            },
            {
                key: "scoreEightyToNinety",
                label: "80-89分",
                prop: "scoreEightyToNinety",
                render: ({ row }) => text(row.scoreEightyToNinety)
            },
            {
                key: "scoreSeventyToEighty",
                label: "70-79分",
                prop: "scoreSeventyToEighty",
                render: ({ row }) => text(row.scoreSeventyToEighty)
            },
            {
                key: "scoreSixtyToSeventy",
                label: "60-69分",
                prop: "scoreSixtyToSeventy",
                render: ({ row }) => text(row.scoreSixtyToSeventy)
            },
            {
                key: "invitationQuotationCount",
                label: "邀请报价数",
                prop: "invitationQuotationCount",
                render: ({ row }) => text(row.invitationQuotationCount)
            },
            {
                key: "viewRequirementNum",
                label: "查看需求数",
                prop: "viewRequirementNum",
                render: ({ row }) => text(row.viewRequirementNum)
            },
            {
                key: "submitQuotationNum",
                label: "提交报价数",
                prop: "submitQuotationNum",
                render: ({ row }) => text(row.submitQuotationNum)
            },
            {
                key: "isGenerateWaybill",
                label: "是否有生成船运单",
                width: 148,
                prop: "isGenerateWaybill",
                render: ({ row }) => enumText(IS_ENTER, row.isGenerateWaybill)
            }
        ],
        []
    );

    return (
        <section aria-label="ship-analysis-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <h2 className="m-0">匹配船舶分析</h2>
                <p>列表数据为按需求查船、发短信及后续查看和报价操作的埋点数据</p>
                <Space wrap>
                    <Select
                        aria-label="操作端"
                        allowClear
                        placeholder="请选择操作端"
                        value={filters.operationPlatform}
                        options={SHIP_PLATFORM.options.map(item => ({ label: item.label, value: Number(item.value) }))}
                        onChange={value => setFilters(current => ({ ...current, operationPlatform: value }))}
                    />
                    <Select
                        aria-label="功能模块"
                        allowClear
                        placeholder="请选择功能模块"
                        value={filters.functionModule}
                        options={FUNCTION_MODULE.options.map(item => ({
                            label: item.label,
                            value: Number(item.value)
                        }))}
                        onChange={value => setFilters(current => ({ ...current, functionModule: value }))}
                    />
                    <Input
                        aria-label="操作开始时间"
                        placeholder="操作开始时间"
                        type="date"
                        value={filters.startDate}
                        onChange={event => setFilters(current => ({ ...current, startDate: event.target.value }))}
                    />
                    <Input
                        aria-label="操作结束时间"
                        placeholder="操作结束时间"
                        type="date"
                        value={filters.endDate}
                        onChange={event => setFilters(current => ({ ...current, endDate: event.target.value }))}
                    />
                    <Input
                        aria-label="匹配货源结果数（最小值）"
                        placeholder="匹配货源结果数（最小值）"
                        type="number"
                        min={0}
                        value={filters.resultCountMin}
                        onChange={event => setFilters(current => ({ ...current, resultCountMin: event.target.value }))}
                    />
                    <Input
                        aria-label="匹配货源结果数（最大值）"
                        placeholder="匹配货源结果数（最大值）"
                        type="number"
                        min={0}
                        value={filters.resultCountMax}
                        onChange={event => setFilters(current => ({ ...current, resultCountMax: event.target.value }))}
                    />
                    <Select
                        aria-label="是否生成船运单"
                        allowClear
                        placeholder="是否生成船运单"
                        value={filters.isGenerateWaybill}
                        options={IS_ENTER.options.map(item => ({ label: item.label, value: Number(item.value) }))}
                        onChange={value => setFilters(current => ({ ...current, isGenerateWaybill: value }))}
                    />
                    <Select
                        aria-label="匹配分数"
                        allowClear
                        placeholder="请选择匹配分数"
                        value={filters.score}
                        options={matchScoreOptions}
                        onChange={value => setFilters(current => ({ ...current, score: value }))}
                    />
                    <Button type="primary" aria-label="查询" onClick={submit}>
                        查询
                    </Button>
                    <Button aria-label="重置" onClick={reset}>
                        重置
                    </Button>
                </Space>
            </Card>
            <Card>
                <ZhTable<ShipAnalysisRow>
                    rowKey={row => String(row.trackId || "ship-analysis")}
                    data={rows}
                    columns={analysisColumns}
                    pagination={false}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            {error ? <Alert role="alert" type="error" message={error} /> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        // 切换每页条数后回到第一页，普通翻页保留当前页。
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}
